import { AxiosError, AxiosResponse } from 'axios'
import TextFieldWithUnit from 'common/components/TextFieldWithUnit'
import ThaiDatePicker from 'common/components/ThaiDatePicker'
import useAnalytics, { AnalyticsEvents } from 'common/hooks/useAnalytics'
import useYupValidationResolver from 'common/hooks/useYupValidationResolver'
import { formatDate } from 'common/utils/date'
import { errorMessageByField } from 'common/utils/hook-form'
import { numberSchema } from 'common/utils/yup'
import endOfToday from 'date-fns/endOfToday'
import parseISO from 'date-fns/parseISO'
import { capitalize } from 'lodash'
import BaseRecordDialog from 'modules/pond/components/BaseRecordDialog'
import { useSpeciesName } from 'modules/pond/api/selector'
import { useMutateCreateWaterQuality, useMutatePatchWaterQuality } from 'modules/water/api/mutation'
import { useGetWaterQualityByDate } from 'modules/water/api/queries'
import {
  QualityTableRow,
  WaterEditData,
  WaterQualityRecord,
  WaterQualityValue,
} from 'modules/water/api/types'
import {
  DayNight,
  PeriodOfDay,
  QualityOption,
  WATER_QUALITY_OPTIONS,
} from 'modules/water/constants'
import { waterDateFilterState } from 'modules/water/stores/atom'
import { normalizeSpeciesName } from 'utils/analytics'
import { enqueueSnackbar } from 'notistack'
import { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useRecoilValue } from 'recoil'
import * as Yup from 'yup'

import {
  Box,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  useTheme,
} from '@mui/material'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'

interface WaterQualityDialogProps {
  onClose: () => void
  open: boolean
  isEdit?: boolean
  onSuccessSave?: () => void
  editData?: WaterEditData
}

interface FormDataType {
  date: Date
  salinityDay: string
  salinityNight: string
  pHDay: string
  pHNight: string
  temperatureDay: string
  temperatureNight: string
  DODay: string
  DONight: string
  alkaliDay: string
  alkaliNight: string
  nitriteDay: string
  nitriteNight: string
  ammoniaDay: string
  ammoniaNight: string
  terbidityDay: string
  terbidityNight: string
}

const WaterQualityDialog = ({ onClose, open, isEdit, editData }: WaterQualityDialogProps) => {
  const { t } = useTranslation('water')
  const theme = useTheme()
  const [submitting, setSubmitting] = useState(false)

  const date = useRecoilValue(waterDateFilterState)

  const qualityEditData = editData as QualityTableRow | undefined

  const createWaterQualityMutation = useMutateCreateWaterQuality()
  const updateWaterQualityMutation = useMutatePatchWaterQuality()
  const [lookupDate, setLookupDate] = useState<Date | null>(isEdit ? null : (date ?? new Date()))
  const { data: byDateData } = useGetWaterQualityByDate(lookupDate)
  const speciesName = useSpeciesName()
  const { trackEvent } = useAnalytics()

  const getWaterQualityObj = (want: 'schema' | 'init') => {
    let value: Yup.StringSchema | string = ''
    if (want === 'schema') {
      value = numberSchema(t('require.number'))
    }
    const dayNight = Object.values(DayNight).map((time) =>
      Object.fromEntries(
        Object.values(QualityOption).map((val) => [`${val}${capitalize(time)}`, value])
      )
    )
    return dayNight.reduce((acc, curr) => ({ ...acc, ...curr }), {})
  }
  const schema = Yup.object().shape({
    date: Yup.date(),
    ...getWaterQualityObj('schema'),
  })

  const resolver = useYupValidationResolver(schema)

  const {
    control,
    handleSubmit,
    reset,
    setError,
    formState: { errors },
  } = useForm<FormDataType>({
    defaultValues: {
      date: new Date(),
      ...getWaterQualityObj('init'),
    },
    resolver,
  })

  useEffect(() => {
    if (date) {
      reset((values) => ({
        ...values,
        date: date,
      }))
    }
  }, [date])

  useEffect(() => {
    if (qualityEditData && isEdit) {
      reset((_v) => ({
        date: parseISO(qualityEditData.date),
        salinityDay: qualityEditData.salinityMorning,
        salinityNight: qualityEditData.salinityEvening,
        pHDay: qualityEditData.pHMorning,
        pHNight: qualityEditData.pHEvening,
        temperatureDay: qualityEditData.temperatureMorning,
        temperatureNight: qualityEditData.temperatureEvening,
        DODay: qualityEditData.DOMorning,
        DONight: qualityEditData.DOEvening,
        alkaliDay: qualityEditData.alkaliMorning,
        alkaliNight: qualityEditData.alkaliEvening,
        nitriteDay: qualityEditData.nitriteMorning,
        nitriteNight: qualityEditData.nitriteEvening,
        ammoniaDay: qualityEditData.ammoniaMorning,
        ammoniaNight: qualityEditData.ammoniaEvening,
        terbidityDay: qualityEditData.terbidityMorning,
        terbidityNight: qualityEditData.terbidityEvening,
      }))
    }
  }, [qualityEditData, isEdit])

  // A date with no existing record still returns 200 with every field null,
  // so every field is reset (not only when non-null) to avoid leaving a
  // previous date's typed values behind.
  useEffect(() => {
    if (isEdit || !byDateData) return

    const { morning, evening } = byDateData
    const qualityValues = WATER_QUALITY_OPTIONS.reduce((acc, { category }) => {
      const key = category as keyof WaterQualityRecord
      const morningValue = morning?.[key]
      const eveningValue = evening?.[key]
      return {
        ...acc,
        [`${category}Day`]: morningValue === null || morningValue === undefined ? '' : String(morningValue),
        [`${category}Night`]: eveningValue === null || eveningValue === undefined ? '' : String(eveningValue),
      }
    }, {} as Partial<FormDataType>)

    reset((values) => ({ ...values, ...qualityValues }))
  }, [byDateData, isEdit])

  const formList = (
    <TableContainer
      component={Paper}
      variant="outlined"
      sx={{
        mt: 2,
        minWidth: '550px',
        borderBottom: 'none',
        borderBottomLeftRadius: 0,
        borderBottomRightRadius: 0,
      }}
    >
      <Table size="small">
        <TableHead>
          <TableRow sx={{ bgcolor: theme.palette.grey[200] }}>
            <TableCell sx={{ fontWeight: 600, minWidth: '110px' }}></TableCell>
            <TableCell
              sx={{ fontWeight: 600, fontSize: 16, color: theme.palette.text.primary, py: 1 }}
              align="left"
            >
              {t('morning.header')}
            </TableCell>
            <TableCell
              sx={{ fontWeight: 600, fontSize: 16, color: theme.palette.text.primary, py: 1 }}
              align="left"
            >
              {t('evening.header')}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {WATER_QUALITY_OPTIONS.map((form, index) => {
            const morningFieldName = `${form.category}${capitalize(
              DayNight.Day
            )}` as keyof FormDataType
            const eveningFieldName = `${form.category}${capitalize(
              DayNight.Night
            )}` as keyof FormDataType

            return (
              <TableRow
                key={`${form.category}-${index}`}
                sx={{
                  '&:not(:last-child)': {
                    '& td': {
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                    },
                  },
                }}
              >
                <TableCell>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography>{t(form.label)}</Typography>
                  </Stack>
                </TableCell>
                <TableCell>
                  <TextFieldWithUnit
                    name={morningFieldName}
                    control={control}
                    errors={errors}
                    placeholder={t('morning.header')}
                    unit={form.unit}
                  />
                </TableCell>
                <TableCell>
                  <TextFieldWithUnit
                    name={eveningFieldName}
                    control={control}
                    errors={errors}
                    placeholder={t('evening.header')}
                    unit={form.unit}
                  />
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </TableContainer>
  )

  const onSuccess = (res: AxiosResponse) => {
    const { status } = res.data
    if (status === 200) {
      setSubmitting(false)
      onClose()
      reset()
      enqueueSnackbar(t('common:save.success'), { variant: 'success' })
      const normalizedSpecies = normalizeSpeciesName(speciesName)
      trackEvent(isEdit ? AnalyticsEvents.waterQualityRecordUpdated : AnalyticsEvents.waterQualityRecordCreated, {
        water_record_type: 'quality',
        ...(normalizedSpecies ? { species: normalizedSpecies } : {}),
      })
    }
  }

  const onError = (error: unknown, _variables: any, _context: unknown) => {
    const { response } = error as AxiosError
    if (response?.status === 400) {
      setError('date', {
        type: 'server',
        message: t('water:duplicate.record'),
      })
    }
  }

  const onSubmit = async (data: FormDataType) => {
    setSubmitting(true)

    const formattedDate = formatDate(data.date)
    const dayArr = WATER_QUALITY_OPTIONS.map((el) => {
      const key = `${el.category}${capitalize(DayNight.Day)}` as keyof FormDataType
      const raw = data[key]
      return { [el.category]: raw === '' || raw === undefined || raw === null ? null : Number(raw) }
    })
    const dayObj = dayArr.reduce((acc, curr) => ({ ...acc, ...curr }), {})

    const nightArr = WATER_QUALITY_OPTIONS.map((el) => {
      const key = `${el.category}${capitalize(DayNight.Night)}` as keyof FormDataType
      const raw = data[key]
      return { [el.category]: raw === '' || raw === undefined || raw === null ? null : Number(raw) }
    })

    const nightObj = nightArr.reduce((acc, curr) => ({ ...acc, ...curr }), {})
    const measurementTimes = [
      {
        timeOfDay: PeriodOfDay.Morning,
        waterQualityValues: dayObj as any as WaterQualityValue,
      },
      {
        timeOfDay: PeriodOfDay.Evening,
        waterQualityValues: nightObj as any as WaterQualityValue,
      },
    ]

    if (isEdit) {
      await updateWaterQualityMutation
        .mutateAsync(
          {
            body: {
              date: formattedDate,
              measurementTimes,
            },
          },
          { onSuccess, onError }
        )
        .finally(() => setSubmitting(false))
      return
    }
    await createWaterQualityMutation
      .mutateAsync(
        {
          body: {
            date: formattedDate,
            measurementTimes,
          },
        },
        { onSuccess, onError }
      )
      .finally(() => setSubmitting(false))
  }

  return (
    <BaseRecordDialog
      onClose={onClose}
      open={open}
      title={t('water:record.water.quality')}
      formId="record-water-quality"
      isSubmitting={submitting}
      isShowPondName={false}
    >
      <form id="record-water-quality" onSubmit={handleSubmit(onSubmit)}>
        <Stack spacing={2}>
          <Grid item xs={6}>
            <Box sx={{ paddingTop: 1, mb: 1 }}>
              <Typography fontSize={16} fontWeight={600}>
                <Typography component="span" color="error" sx={{ mr: 0.5 }}>
                  *
                </Typography>
                {t('common:date')}
              </Typography>
            </Box>

            <Controller
              name="date"
              control={control}
              render={({ field }) => (
                <Grid item xs={8}>
                  <ThaiDatePicker
                    {...field}
                    onChange={(newDate) => {
                      field.onChange(newDate)
                      if (!isEdit) setLookupDate(newDate)
                    }}
                    format="dd/MM/yyyy"
                    maxDate={endOfToday()}
                    slotProps={{
                      textField: {
                        id: 'quality-date-picker',
                        size: 'small',
                        error: !!errors.date,
                        helperText: errorMessageByField('date', errors),
                        fullWidth: true,
                      },
                    }}
                    readOnly={isEdit}
                  />
                </Grid>
              )}
            />
          </Grid>
          {formList}
        </Stack>
      </form>
    </BaseRecordDialog>
  )
}

export default WaterQualityDialog
